/**
 * @file statementRunKind.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-20
 * @modified 2026-10-20
 * @description A statement run's kind (month-end or interim), what an interim
 *              statement's "Since" section shows, the PDF contents a run
 *              starts with, and which customers a send emails and which it
 *              prints — one rule for Generate Statements and the server
 *              (utils/statementRunKind.js is the generated server copy;
 *              scripts/sync-shared-utils.js).
 *
 *   Cam, 20 Oct 2026 (Statement Runs Redesign, the picks on the proposal):
 *
 *   - A run is MONTH-END or INTERIM, chosen on the first screen. An interim
 *     run is still a whole month's statement "as at" that month's end —
 *     usually last month, re-issued after something changed it (credits
 *     raised and allocated for price queries, payments made before the month
 *     end and reconciled late) or when the customer needs more detail
 *     (allocated credits listed, overdue invoices itemised and attached). So
 *     its month must have ended.
 *   - An interim statement shows the month as at its end — credit
 *     allocations made after it are taken off as well as payments, so the
 *     month reads as it stood — then "Since <month end>": the payments,
 *     allocations and refunds since that touch the documents on the
 *     statement, and what those documents come to now.
 *   - Labelled as the month-end one with "(Interim)" after it.
 *   - Not an interim's business: the Accounts step (Worldpay tick, hand over
 *     to Accounts, the send gate), the "orders that missed statements"
 *     notice, the "paid just after the period end" flag. Its Xero check is
 *     for information: shown, never blocking or un-approving.
 *   - Fully-used credit notes (allocated or refunded in full) are neither
 *     listed nor attached unless the run says so — on both kinds.
 *   - A send can go by contact group: "Email Invoices" members are emailed
 *     (email layout), "Postal Invoices" members printed (print layout), a
 *     member of both gets both; anyone in neither is chosen on the screen.
 *     Emails go first, then the print.
 */

export const RUN_KINDS = ['month_end', 'interim'];
export const DEFAULT_RUN_KIND = 'month_end';

/** 'interim' when it says so, else 'month_end' (a run from before the column, or nonsense). */
export function normaliseRunKind(value) {
  const v = typeof value === 'object' && value !== null ? (value.run_kind ?? value.runKind ?? value.kind) : value;
  return v === 'interim' ? 'interim' : 'month_end';
}

export function isInterim(value) {
  return normaliseRunKind(value) === 'interim';
}

export const RUN_KIND_LABEL = { month_end: 'Month-end', interim: 'Interim' };

export const RUN_KIND_HELP = {
  month_end: 'Every customer’s statement for a month. Accounts confirm before it goes out.',
  interim: 'A month’s statement re-issued after it went out — as at its month end, then what has changed since.'
};

/** The statement's title on the PDF. */
export function statementTitle(kind) {
  return isInterim(kind) ? 'Statement of Account (Interim)' : 'Statement of Account';
}

/** Appended to a statement email's subject. */
export function subjectSuffix(kind) {
  return isInterim(kind) ? ' (Interim)' : '';
}

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function lastDayOfMonth(y, m) {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** True when start..end is exactly one calendar month ('YYYY-MM-DD'). */
export function isWholeMonth(dates) {
  const s = DAY_RE.exec(String(dates?.start || ''));
  const e = DAY_RE.exec(String(dates?.end || ''));
  if (!s || !e) return false;
  if (s[1] !== e[1] || s[2] !== e[2] || s[3] !== '01') return false;
  return Number(e[3]) === lastDayOfMonth(Number(e[1]), Number(e[2]));
}

/**
 * Why this run cannot be generated as `kind` for these dates, or null.
 * `today` is the London day 'YYYY-MM-DD'; `ready` false = the migration has not run.
 */
export function runKindBlock({ kind, dates, today, ready = true }) {
  if (!isInterim(kind)) return null;
  if (!ready) return 'Not ready — migration statement_run_kind.sql has not run';
  if (!isWholeMonth(dates)) return 'An interim statement is a whole month’s statement — choose a month';
  if (today && String(dates.end) >= String(today)) {
    return 'An interim statement re-issues a month that has ended — choose last month or earlier';
  }
  return null;
}

/** The kind a set of dates suggests when nobody has chosen: always month-end (interim is chosen on purpose). */
export function suggestedRunKind() {
  return DEFAULT_RUN_KIND;
}

// ── What goes inside each PDF ────────────────────────────────────────────────

/** The Contents step's choices a run starts with (Cam: credit notes off on both kinds). */
export const CONTENT_DEFAULTS = Object.freeze({
  include: 'both',
  includeAllUnpaidInvoices: false,
  suppressOverdueStamp: true,
  listUsedCredits: false,
  attachUsedCredits: false
});

const PENNY = 0.005;
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * A credit note listed on the statement although nothing is left of it:
 * how it was used, in words ("Allocated to INV-16188", "Refunded").
 * `uses`: [{ kind: 'allocation'|'refund', invoiceNo?, amount }]
 */
export function usedCreditText(uses) {
  const list = Array.isArray(uses) ? uses : [];
  const allocated = [...new Set(list.filter(u => u.kind === 'allocation' && u.invoiceNo).map(u => u.invoiceNo))];
  const refunded = list.some(u => u.kind === 'refund');
  const parts = [];
  if (allocated.length) parts.push(`Allocated to ${allocated.join(', ')}`);
  if (refunded) parts.push(allocated.length ? 'refunded' : 'Refunded');
  return parts.join('; ') || 'Fully used';
}

/**
 * The statement's rows with the fully-used credit notes put in among the
 * period's rows (by date), each with a zero balance. The totals do not move.
 * `transactions`: the stored rows; `usedCredits`: data.usedCredits.
 */
export function withUsedCredits(transactions, usedCredits) {
  const rows = Array.isArray(transactions) ? [...transactions] : [];
  const extra = (Array.isArray(usedCredits) ? usedCredits : []).filter(c => c && c.activity);
  if (!extra.length) return rows;
  const have = new Set(rows.filter(r => !r.isBalanceForward).map(r => r.activity));
  const add = extra.filter(c => !have.has(c.activity)).map(c => ({
    date: c.date,
    activity: c.activity,
    reference: c.reference || usedCreditText(c.uses),
    dueDate: c.dueDate || c.date,
    amount: round2(c.amount),
    payment: round2(c.amount),
    balance: 0,
    isCredit: true,
    isUsedCredit: true
  }));
  const forward = rows.filter(r => r.isBalanceForward);
  const period = rows.filter(r => !r.isBalanceForward).concat(add);
  period.sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')) || String(a.activity || '').localeCompare(String(b.activity || '')));
  return forward.concat(period);
}

// ── An interim statement's "Since" section ───────────────────────────────────

/**
 * What changed since the month end, for the documents on the statement.
 *
 * @param {Object} p
 * @param {number} p.balanceAtEnd - the statement's total as at the month end
 * @param {Map<number,{docNo:string,kind:'invoice'|'credit'}>|Object} p.onStatement
 *        the documents with something outstanding on the statement, by id
 * @param {Array} p.events - after the month end, oldest first:
 *        { type: 'payment', date, amount, docId, docNo }   (a payment on a document; on a credit it is a refund)
 *        { type: 'allocation', date, amount, creditId, creditNo, creditDate, invoiceId, invoiceNo }
 * @returns {{ lines: Array<{date,text,docNo,amount}>, balanceNow: number, count: number }}
 *   amount is the effect on what the statement's documents come to: a
 *   payment or credit against an invoice on it takes off; a credit on it used
 *   elsewhere, or refunded, adds back; a credit on it applied to an invoice
 *   on it moves nothing (listed, so the customer can see it).
 */
export function sinceSection({ balanceAtEnd, onStatement, events }) {
  const has = (id) => (onStatement instanceof Map ? onStatement.has(Number(id)) : !!(onStatement && onStatement[id]));
  const get = (id) => (onStatement instanceof Map ? onStatement.get(Number(id)) : onStatement?.[id]);
  const lines = [];
  for (const e of Array.isArray(events) ? events : []) {
    const amount = round2(e.amount);
    if (!(Math.abs(amount) > PENNY)) continue;
    if (e.type === 'payment') {
      if (!has(e.docId)) continue;
      const doc = get(e.docId);
      const credit = doc?.kind === 'credit';
      lines.push({
        date: e.date,
        docNo: e.docNo || doc?.docNo || '',
        text: credit ? `Refund of ${e.docNo || doc?.docNo}` : `Payment received for ${e.docNo || doc?.docNo}`,
        amount: credit ? amount : -amount
      });
    } else if (e.type === 'allocation') {
      const invOn = has(e.invoiceId);
      const crOn = has(e.creditId);
      if (!invOn && !crOn) continue;
      const effect = round2((crOn ? amount : 0) - (invOn ? amount : 0));
      const creditName = crOn ? e.creditNo : `${e.creditNo}${e.creditDate ? ` (${dmy(e.creditDate)})` : ''}`;
      lines.push({
        date: e.date,
        docNo: invOn ? e.invoiceNo : e.creditNo,
        text: `${creditName} applied to ${e.invoiceNo}`,
        amount: effect
      });
    }
  }
  lines.sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
  const moved = lines.reduce((s, l) => s + l.amount, 0);
  return { lines, balanceNow: round2((Number(balanceAtEnd) || 0) + moved), count: lines.length };
}

function dmy(d) {
  const m = DAY_RE.exec(String(d || '').slice(0, 10));
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(d || '');
}

// ── Who is emailed and who is printed ────────────────────────────────────────

export const EMAIL_GROUP = 'Email Invoices';
export const POSTAL_GROUP = 'Postal Invoices';

/**
 * Where one approved statement goes on a send by contact group.
 * `stmt.groups`: { email: bool, postal: bool }; `stmt.email`: the address it
 * would be emailed to; `choice` (for one in neither group, or overriding):
 * 'email' | 'print' | 'both' | 'skip'.
 * @returns {{ email: boolean, print: boolean, why: string, needsChoice: boolean, noAddress: boolean }}
 */
export function routeFor(stmt, choice = null) {
  const g = stmt?.groups || {};
  const hasAddress = !!String(stmt?.email || '').trim();
  let email;
  let print;
  let why;
  if (choice) {
    email = choice === 'email' || choice === 'both';
    print = choice === 'print' || choice === 'both';
    why = choice === 'skip' ? 'Left out on this send' : 'Chosen here';
  } else if (g.email || g.postal) {
    email = !!g.email;
    print = !!g.postal;
    why = g.email && g.postal ? `${EMAIL_GROUP} and ${POSTAL_GROUP}` : (g.email ? EMAIL_GROUP : POSTAL_GROUP);
  } else {
    return { email: false, print: false, why: 'In neither group — choose', needsChoice: true, noAddress: !hasAddress };
  }
  // Emailed with no address: printed instead, and said.
  if (email && !hasAddress) {
    return { email: false, print: true, why: `${why} — no email address, so printed`, needsChoice: false, noAddress: true };
  }
  return { email, print, why, needsChoice: false, noAddress: !hasAddress };
}

/**
 * The whole send: which approved statements are emailed, which printed.
 * `choices`: { [statementId]: 'email'|'print'|'both'|'skip' }.
 * @returns {{ emailIds: number[], printIds: number[], both: number, neither: number[], noAddress: number[], skipped: number[] }}
 */
export function routingPlan(statements, choices = {}) {
  const emailIds = [];
  const printIds = [];
  const neither = [];
  const noAddress = [];
  const skipped = [];
  let both = 0;
  for (const s of Array.isArray(statements) ? statements : []) {
    if (s.approval_status !== 'approved') continue;
    const r = routeFor(s, choices[s.id] || null);
    if (r.needsChoice) { neither.push(s.id); continue; }
    if (!r.email && !r.print) { skipped.push(s.id); continue; }
    if (r.email) emailIds.push(s.id);
    if (r.print) printIds.push(s.id);
    if (r.email && r.print) both += 1;
    if (r.noAddress && r.print && (s.groups?.email)) noAddress.push(s.id);
  }
  return { emailIds, printIds, both, neither, noAddress, skipped };
}
